import Select from 'react-select';
import React from 'react';
import { Col, Form, Row } from 'react-bootstrap-5';
import PropTypes from 'prop-types';

import DocumentProgress from './index/table/cells/document_progress';
import { sharedLeadFormProps } from './lead_form_props';

function listingPrefix(listing) {
  switch (listing.type) {
  case 'SaleListing':
    return 'Sale Listing';
  case 'LeaseSpaceListing':
    return 'Lease Space';
  case 'LotListing':
    return 'Lot';
  case 'CondoListing':
    return 'Condo';
  default:
    return 'No Listing';
  }
}

function listingLabel(listing) {
  if (!listing.id) return listingPrefix(listing);

  const prefix = listingPrefix(listing);

  return listing.type === 'SaleListing' ? prefix : `${prefix} - ${listing.address2 || listing.number}`;
}

export default function LeadFormFields({
  docAccessLevels,
  listings,
  lead,
  leadData,
  leadReferralSources,
  leadStatuses,
  leaseChecked,
  project,
  saleChecked,
  useVaultGates,
  onFieldChange
}) {
  const multipleListingsPresent = listings.length > 1;
  const isEditAndNonListing = lead?.listingId &&
    listings.length === 1 &&
    lead.listingId !== listings[0].id;

  const accessLevels = [];
  if (saleChecked) {
    accessLevels.push({
      type: 'Sale',
      value: leadData.saleAccessLevel,
      onChange: value => onFieldChange(value, 'saleAccessLevel'),
      onChangeForVaultGates: value => onFieldChange(value, 'docAccessLevelForSale')
    });
  }
  if (leaseChecked) {
    accessLevels.push({
      type: 'Lease',
      value: leadData.leaseAccessLevel,
      onChange: value => onFieldChange(value, 'leaseAccessLevel'),
      onChangeForVaultGates: value => onFieldChange(value, 'docAccessLevelForLease'),
    });
  }

  return (
    <React.Fragment>
      <Form.Group className="form-group">
        <Form.Label>Status</Form.Label>
        <Select
          className="form-group"
          getOptionValue={options => options.id}
          menuPlacement="auto"
          menuPortalTarget={document.querySelector('body')}
          name="lead-status"
          options={leadStatuses}
          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          value={leadData.status}
          onChange={status => onFieldChange(status, 'status')}
        />
      </Form.Group>

      <Form.Group className="form-group">
        <Form.Label>
          Referral Source {!lead && (<span className="text-danger"> (Required)</span>)}
        </Form.Label>
        {lead && lead.referralSourceFixed ? (
          <div className="form-group">{lead.referralSourceLabel} (locked)</div>
        ) : (
          <Select
            className="form-group"
            getOptionValue={options => options.id}
            menuPlacement="auto"
            menuPortalTarget={document.querySelector('body')}
            name="leadReferralSource"
            options={leadReferralSources}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            value={leadData.referralSource}
            onChange={referralSource => onFieldChange(referralSource, 'referralSource')}
          />
        )}
      </Form.Group>

      {(isEditAndNonListing || multipleListingsPresent) && (
        <Form.Group className="form-group">
          <Form.Label>Space</Form.Label>
          <Select
            className="form-group"
            getOptionLabel={listingLabel}
            getOptionValue={option => option.id}
            menuPlacement="auto"
            name="lead-listing"
            options={listings}
            value={leadData.listing}
            onChange={newListing => onFieldChange(newListing, 'listing')}
          />
        </Form.Group>
      )}

      {!useVaultGates && accessLevels.length > 0 && (
        <Row>
          {accessLevels.map(accessLevel => (
            <Col className="form-group" key={accessLevel.type} md={accessLevels.length > 1 ? 6 : 12}>
              <Form.Group className="form-group">
                <Form.Label>
                  {useVaultGates ? 'Pin ' : ' '}{accessLevels.length > 1 ? `${accessLevel.type} ` : ' '}Access Level
                </Form.Label>
                <Select menuPlacement="auto" options={docAccessLevels} value={accessLevel.value}
                  onChange={accessLevel.onChange}
                />
              </Form.Group>
            </Col>
          ))}
        </Row>
      )}

      {useVaultGates && <Row>
        {accessLevels.map(accessLevel => (
          <Col className="form-group" key={accessLevel.type} md={accessLevels.length > 1 ? 6 : 12}>
            <Form.Group className="form-group">
              <Form.Label>{accessLevels.length > 1 ? accessLevel.type : ''} Access Level</Form.Label>
              <DocumentProgress
                columnName={`docAccessLevelFor${accessLevel.type}`}
                externalOnUpdate={(_, updatedData) => {
                  accessLevel.onChangeForVaultGates(updatedData[`docAccessLevelFor${accessLevel.type}`]);
                }}
                lead={leadData}
              />
            </Form.Group>
          </Col>
        ))}
      </Row>}

    </React.Fragment>
  );
}

LeadFormFields.propTypes = {
  ...sharedLeadFormProps,
  docAccessLevels: PropTypes.array.isRequired,
  listings: PropTypes.array,
  project: PropTypes.object,
  onFieldChange: PropTypes.func.isRequired
};
